import { memo, useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Input, Textarea, SwitchInput, Spacer } from '@Sage/hoi-poi-ui';
import { useSelector } from 'react-redux';
import { getSrcUserCircleAvatar } from 'utils/getSrcAvatar';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import EmailInput from 'containers/components/Fields/EmailInput';

import './styles.scss';

const AutomationBuilderHeaderModalForm = memo(({ onRef, onSave }) => {
    const saveCb = useRef(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState();
    const [errors, setErrors] = useState({});
    const { userEmail, userName, userId } = useSelector((state) => ({
        userEmail: state?.config?.userData?.email,
        userName: `${state?.config?.userData?.nombre} ${state?.config?.userData?.apellidos}`,
        userId: state?.config?.userData?.idUsuario,
    }));

    useEffect(() => {
        onRef &&
            onRef({
                open(data, onSaveCb) {
                    if (onSaveCb) saveCb.current = onSaveCb;
                    setErrors({});
                    const { src, fallbackSrc } = getSrcUserCircleAvatar(userId);
                    setForm({
                        name: data?.name,
                        description: data?.description,
                        enabledErrorNotification: data.hasOwnProperty('enabledErrorNotification')
                            ? data?.enabledErrorNotification
                            : true,
                        notificationEmail: data?.notificationEmail?.length
                            ? data?.notificationEmail?.map((item) => {
                                  const { src, fallbackSrc } = getSrcUserCircleAvatar(userId);
                                  return {
                                      ...item,
                                      subLabel: item.email,
                                      src,
                                      placeholder: fallbackSrc,
                                  };
                              })
                            : [
                                  {
                                      type: 3,
                                      label: userName,
                                      subLabel: userEmail,
                                      email: userEmail,
                                      value: parseInt(userId, 10),
                                      src,
                                      placeholder: fallbackSrc,
                                  },
                              ],
                    });
                    setOpen(true);
                },
            });
    }, [onRef, userEmail, userId, userName]);

    const onInnerChange = useCallback(
        (field) => (value) => {
            let errors = {};

            if (field === 'name' && value?.length > 80) {
                errors.name = getLiteralWithParameters('error_text_too_long', [80]);
            }

            if (field === 'description' && value?.length > 500) {
                errors.description = getLiteralWithParameters('error_text_too_long', [500]);
            }

            setErrors(errors);
            setForm((current) => ({ ...current, [field]: value }));
        },
        [],
    );

    const onInnerCancel = useCallback(() => {
        setOpen(false);
    }, []);

    const onInnerSave = useCallback(() => {
        setOpen(false);
        if (errors?.name || errors?.description) return;
        if (form?.notificationEmail?.length) {
            form.notificationEmail = form.notificationEmail.map((item) => {
                return {
                    label: item.label,
                    type: item.type,
                    value: item.value,
                    email: item.subLabel || item.email,
                };
            });
        }

        if (!form?.enabledErrorNotification) {
            form.notificationEmail = [];
        }

        onSave(form).then(() => setTimeout(() => saveCb?.current?.(form)));
    }, [errors?.description, errors?.name, form, onSave]);

    const isConfirmDisabled =
        errors?.name ||
        errors?.description ||
        !form?.name ||
        (form?.enabledErrorNotification && !form?.notificationEmail?.length);

    return (
        <Modal
            overlayClassName="fm-automation-builder__header-form"
            isOpen={open}
            onRequestClose={onInnerCancel}
            onCancel={onInnerCancel}
            onConfirm={onInnerSave}
            confirmText={getLiteral('action_save')}
            cancelText={getLiteral('action_cancel')}
            title={getLiteral('label_automation_edit_name')}
            isConfirmDisabled={isConfirmDisabled}
            size="small"
        >
            <Input
                label={getLiteral('label_automation_name')}
                labelMode="vertical"
                placeholder={getLiteral('placeholder_text_field')}
                isRequired
                onChange={onInnerChange('name')}
                value={form?.name}
                error={errors?.name}
                autoFocus
                isFullWidth
            />
            <Textarea
                label={getLiteral('label_automation_description')}
                labelMode="vertical"
                placeholder={getLiteral('placeholder_text_field')}
                onChange={onInnerChange('description')}
                value={form?.description}
                error={errors?.description}
                minRows={4}
                maxRows={10}
                isFullWidth
            />
            <SwitchInput
                label={getLiteral('action_automations_notify_error')}
                onChange={onInnerChange('enabledErrorNotification')}
                value={form?.enabledErrorNotification}
            />

            {form?.enabledErrorNotification && (
                <>
                    <Spacer y={3} />
                    <EmailInput
                        label={getLiteral('label_automation_notify_error_email_to')}
                        placeholder={getLiteral('placeholder_search_hint')}
                        onChange={onInnerChange('notificationEmail')}
                        value={form?.notificationEmail}
                        isRequired
                        entityFilter="Usuario"
                        skipPermissions
                    />
                </>
            )}
        </Modal>
    );
});

export default AutomationBuilderHeaderModalForm;
